<template>
    <Head>
        <title>{{title}}</title>
        <meta name="description" :content="description">
    </Head>
    <div class="layout-container max-w-full">
        <TopBar/>
        <main>
            <slot/>
        </main>
        <Footer/>
    </div>
    <OnlyMobile>
        <MobileMenu/>
        <MobileNav/>
    </OnlyMobile>
    <OnlyDesktop>
        <SpeedDial/>
        <ScrollUp/>
    </OnlyDesktop>
    <CallbackPopup/>
    <FormSuccessPopup/>
    <FormAlreadySentPopup/>
    <ErrorPopup/>
    <Toast/>
</template>

<script>
import {Head} from "@inertiajs/vue3";
import {router} from '@inertiajs/vue3';
import {useThemeStore} from "@js/store/ThemeStore.js";
import TopBar from "@/js/components/TopBar/TopBar.vue";
import Footer from "@/js/components/Footer/Footer.vue";
import MobileMenu from "@/js/components/Mobile/MobileMenu.vue";
import MobileNav from "@/js/components/Mobile/MobileNav.vue";
import SpeedDial from "@js/components/Widgets/SpeedDial.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import FormSuccessPopup from "@js/components/Sections/Popup/FormSuccessPopup.vue";
import ErrorPopup from "@js/components/Sections/Popup/ErrorPopup.vue";
import FormAlreadySentPopup from "@js/components/Sections/Popup/FormAlreadySentPopup.vue";
import CallbackPopup from "@js/components/Sections/Popup/CallbackPopup.vue";
import Toast from "@js/components/Toast/Toast.vue";
import {useCartStore} from "@js/store/CartStore.js";
import {computed, onMounted} from "vue";
import {useMenuStore} from "@js/store/MenuStore.js";
import ScrollUp from "@js/components/Widgets/ScrollUp.vue";

export default {
    name: "Layout",
    components: {
        ScrollUp,
        Toast,
        CallbackPopup,
        FormAlreadySentPopup,
        ErrorPopup,
        FormSuccessPopup,
        OnlyDesktop,
        OnlyMobile,
        SpeedDial,
        MobileNav,
        MobileMenu,
        Footer,
        TopBar,
        Head,
    },
    props: {
        title: String,
        description: String
    },
    mounted() {
        router.on("before", (event) => {
            this.themeStore.resetMap()
            this.menuStore.closeSidenav()
        })
        this.insertOrganizationSchemaMarkup()
        this.insertLocalBusinessSchemaMarkup()
        this.insertNavigationSchemaMarkup()
    },
    methods: {
        insertOrganizationSchemaMarkup() {
            if (typeof window !== "undefined") {
                const scriptId = "schema-organization-jsonld"
                let script = document.getElementById(scriptId)

                if (script) {
                    script.remove()
                }

                script = document.createElement("script")
                script.id = scriptId
                script.type = "application/ld+json"
                script.textContent = JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "ООО «Дорожные плиты»",
                    "url": "https://dorozhnyeplity.ru/",
                    "logo": "https://dorozhnyeplity.ru/assets/logo/logo-rounded-full.png",
                    "description": "Компания ООО «Дорожные плиты» предоставляет высококачественные бетонные плиты для дорожного строительства и инфраструктуры.",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+7 (495) 275-30-77",
                        "contactType": "Customer Service",
                        "areaServed": "RU",
                        "availableLanguage": "Russian"
                    },
                    "sameAs": [
                        "https://api.whatsapp.com/send/?phone=79256649222&text&type=phone_number&app_absent=0",
                        "https://t.me/Dorozhnyeplity",
                        "https://yandex.com/maps/-/CHqCyLLX"
                    ],
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Промышленная ул., 38",
                        "addressLocality": "Домодедово",
                        "addressRegion": "Московская область",
                        "postalCode": "142000",
                        "addressCountry": "RU"
                    },
                })

                document.head.appendChild(script);
            }
        },
        insertLocalBusinessSchemaMarkup() {
            if (typeof window !== "undefined") {
                const scriptId = "schema-local-business-jsonld"
                let script = document.getElementById(scriptId)

                if (script) {
                    script.remove()
                }

                script = document.createElement("script")
                script.id = scriptId
                script.type = "application/ld+json"
                script.textContent = JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "ООО «Дорожные плиты»",
                    "url": "https://dorozhnyeplity.ru/",
                    "logo": "https://dorozhnyeplity.ru/assets/logo/logo-rounded-full.png",
                    "description": "Компания ООО «Дорожные плиты» предоставляет высококачественные бетонные плиты для дорожного строительства и инфраструктуры.",
                    "telephone": "+7 (495) 275-30-77",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Промышленная ул., 38",
                        "addressLocality": "Домодедово",
                        "addressRegion": "Московская область",
                        "postalCode": "142000",
                        "addressCountry": "RU"
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": 55.462540,
                        "longitude": 37.781900
                    },
                    "openingHours": "Mo-Su 08:00-20:00",
                    "openingHoursSpecification": {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                        "opens": "08:00",
                        "closes": "20:00"
                    },
                    "sameAs": [
                        "https://api.whatsapp.com/send/?phone=79256649222&text&type=phone_number&app_absent=0",
                        "https://t.me/Dorozhnyeplity",
                        "https://yandex.com/maps/-/CHqCyLLX",
                    ],
                    "priceRange": "$$"
                })

                document.head.appendChild(script);
            }
        },
        insertNavigationSchemaMarkup() {
            if (typeof window !== "undefined") {
                const scriptId = "schema-navigation-jsonld"
                let script = document.getElementById(scriptId)

                if (script) {
                    script.remove()
                }

                script = document.createElement("script")
                script.id = scriptId
                script.type = "application/ld+json"

                function convertToJSONLD(menuItems) {
                    return menuItems.map(item => {
                        const menuElement = {
                            "@type": "SiteNavigationElement",
                            "name": item.title
                        };

                        // Если есть route_name, добавляем ссылку
                        if (item.route_name) {
                            menuElement.url = `https://dorozhnyeplity.ru/${item.route_name}`;
                        }

                        // Если есть подменю, рекурсивно добавляем его
                        if (item.submenu) {
                            menuElement.hasPart = convertToJSONLD(item.submenu);
                        }

                        return menuElement;
                    });
                }

                script.textContent = JSON.stringify({
                    "@context": "http://schema.org",
                    "@type": "SiteNavigationElement",
                    "name": "Main Navigation",
                    "url": "https://dorozhnyeplity.ru/",
                    "hasPart": convertToJSONLD(this.menuStore.menuItems)
                })

                document.head.appendChild(script);
            }
        }
    },
    setup() {
        const themeStore = useThemeStore()
        const cartStore = useCartStore()
        const menuStore = useMenuStore()

        //Здесь делаем синхронизации с куки и localStorage
        onMounted(() => {
            cartStore.syncCartWithLocalStorage()
        })

        return {themeStore, menuStore}
    }
}
</script>

<style lang="scss" scoped>
.layout-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;

    main {
        flex: 1;
    }
}
</style>
